import firebase from "../../firebase/firebase";
import { DB_COLLECTIONS, DB_DEFAULTS } from "../../firebase/collections";
import { mapUserToSnippet } from "../../utils/mapping";
import { User, UserSnippet } from "../../data/interfaces";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../utils/analytics";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------

// -----------------------------------------------------------------
// A c t i o n s
// -----------------------------------------------------------------
/**
 * Fetches user detail data.
 * @function
 * @async
 *
 * @param user
 */
export const getUserDetail = async (user?: User | null) => {
  try {
    if (!user) {
      return;
    }

    // Get user from firestore
    console.log("[Firebase] read");
    let firestoreUser = await firebase
      .firestore()
      .collection(DB_COLLECTIONS.USERS_DETAILS)
      .doc(user.uid)
      .get();

    // Merge firebase Auth User with Firestore Data
    let userData: any = {
      ...user,
      ...firestoreUser.data(),
    };

    return userData;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Updates user detail data
 * @function
 * @async
 *
 * @param user
 * @param data
 */
export const updateUserDetail = async (user?: User | null, data?: any) => {
  try {
    if (!user || !data) {
      return;
    }

    // Write user to firestore
    console.log("[Firebase] write");
    await firebase.firestore().collection(DB_COLLECTIONS.USERS_DETAILS).doc(user.uid).update(data);
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Updates user profile picture
 * @function
 * @async
 *
 * @param url image url
 * @param uid user uid
 */
export const updateUserProfilePicture = async (url: string, uid: string | undefined) => {
  try {
    if (!uid || !url) {
      return;
    }

    // Write user to firestore
    console.log("[Firebase] write - update user profile pic");
    await firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(uid).update({
      profile_image_url: url,
    });
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Fetches business cards left to stands by user
 * @function
 * @async
 *
 * @param uid
 * @param limit
 * @param last
 */
export const getUserBusinessCards = async (uid: string | undefined, limit?: number, last?: any) => {
  try {
    if (!uid) {
      return [];
    }

    // Load stands from firestore
    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    console.log("[FIREBASE READ], business_cards");
    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.BUSINESS_CARDS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("user.uid", "==", uid)
        .orderBy("left_datetime", "desc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("user.uid", "==", uid)
        .orderBy("left_datetime", "desc")
        .startAfter(last.left_datetime)
        .limit(limit)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Search users from firebase
 * @function
 * @async
 *
 * @param query
 * @param limit
 * @param last
 */
export const searchUsers = async (query: string, limit?: number, last?: any): Promise<any> => {
  try {

    if (!query || query.length < 3) {
      return [];
    }

    console.log("Search users: track", query);


    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.USERS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("keywords", "array-contains", query.toLowerCase())
        .orderBy("lastname", "asc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("keywords", "array-contains", query.toLowerCase())
        .orderBy("lastname", "asc")
        .startAfter(last.lastname)
        .limit(limit)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push(
        mapUserToSnippet({
          uid: doc.id,
          ...doc.data(),
        }),
      );
    });

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Add user to contacts
 * @function
 * @async
 *
 * @param contact user to add to contacts
 * @param user current logged in user
 */
export const addUserToContacts = async (contact: UserSnippet, user: User) => {
  try {
    console.log(contact, user);

    let userRef = firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(user.uid);

    // Add contact id to user contacts
    await userRef.update({
      contacts: firebase.firestore.FieldValue.arrayUnion(contact.uid),
    });

    // Create a record with combined keys in contacts
    let combinedKey: string = `${user.uid}_${contact.uid}`;

    // Create record on firestore
    await firebase
      .firestore()
      .collection(DB_COLLECTIONS.CONTACTS)
      .doc(combinedKey)
      .set(
        {
          added_datetime: firebase.firestore.Timestamp.now(),
          user: mapUserToSnippet(user),
          contact: contact,
        },
        { merge: true },
      );

    // Track event
    trackAnalyticsEvent(ANALYTICS_EVENTS.ADD_TO_CONTACTS, { from: user.email, to: contact.email });
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Remove user from contacts
 * @function
 * @async
 *
 * @param contact user to remove from contacts
 * @param user current logged in user
 */
export const removeUserFromContacts = async (contact: UserSnippet, user: User) => {
  try {
    let userRef = firebase.firestore().collection(DB_COLLECTIONS.USERS).doc(user.uid);

    // Remove contact id from user contacts
    await userRef.update({
      contacts: firebase.firestore.FieldValue.arrayRemove(contact.uid),
    });

    // Delete record from firestore
    let combinedKey: string = `${user.uid}_${contact.uid}`;

    let contactRef = firebase.firestore().collection(DB_COLLECTIONS.CONTACTS).doc(combinedKey);
    await contactRef.delete();
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Fetches user contacts
 * @function
 * @async
 *
 * @param uid
 */
export const getUserContacts = async (uid: string | undefined, limit?: number, last?: any) => {
  try {
    if (!uid) {
      return [];
    }

    // Load stands from firestore
    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    console.log("[FIREBASE READ], contacts");
    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.CONTACTS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("user.uid", "==", uid)
        .orderBy("added_datetime", "desc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("user.uid", "==", uid)
        .orderBy("added_datetime", "desc")
        .startAfter(last.added_datetime)
        .limit(limit)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};
